import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointHomeAddressGetInput,
  type EnrollmentJointHomeAddressData,
  type EnrollmentJointHomeAddressSubmitInput,
  type EnrollmentJointHomeAddressSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint HomeAddress data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointHomeAddressData>
 * @example const response = await getEnrollmentJointHomeAddressData()
 */
export const getEnrollmentJointHomeAddressData = async (
  enrollmentHomeAddressGetInput?: EnrollmentJointHomeAddressGetInput,
  context?: DefaultContext,
): Promise<EnrollmentJointHomeAddressData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointHomeAddressData',
      query: `mutation getEnrollmentJointHomeAddressData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          accountType
          activePerson {
            applicationId
            path
            progress
            name {
              firstName
              lastName
            }
            addressLine1
            addressLine2
            addressCity
            addressRegionCode
            addressPostalCode
            addressCountry
          }
          jointPerson {
            addressLine1
            addressLine2
            addressCity
            addressRegionCode
            addressPostalCode
            addressCountry
            name {
              firstName
            }
          }
        }
      }`,
    },
    {
      input: enrollmentHomeAddressGetInput ?? {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointHomeAddressData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointHomeAddressData = createQueryHook<
  EnrollmentJointHomeAddressGetInput,
  EnrollmentJointHomeAddressData
>(getEnrollmentJointHomeAddressData)

/**
 * Submit Enrollment Joint HomeAddress data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointHomeAddressSubmitOutput>
 * @example const response = await submitEnrollmentJointHomeAddressData();
 */
export const submitEnrollmentJointHomeAddressData = async (
  enrollmentHomeAddressSubmitInput: EnrollmentJointHomeAddressSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointHomeAddressSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointHomeAddressData',
      query: `mutation submitEnrollmentJointHomeAddressData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentHomeAddressSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointHomeAddressSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointHomeAddressData = createMutationHook<
  EnrollmentJointHomeAddressSubmitInput,
  EnrollmentJointHomeAddressSubmitOutput
>(submitEnrollmentJointHomeAddressData)
